import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import CompanyAvatar from '@app/src/pages/ResourceCollection/Collections/CellComponents/CompanyAvatar'
import type { SimpleOrganization } from '@app/src/types/organizations'
import type { ReportAccessModel, Request } from '@app/src/types/resourceExplorer'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import { Language } from '@mui/icons-material'
import { Avatar, List, ListItem, ListItemAvatar, ListItemText, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface SidebarAccessorsListProps {
  request: Request | undefined
}

const SidebarAccessorsList: React.FC<SidebarAccessorsListProps> = ({ request }) => {
  const { formatMessage } = useIntl()

  const { data: currentAccess } = useFetchResource<ReportAccessModel>({
    key: [FetchKey.ResponseSharing, Number(request?.id)],
    endpoint: endpoints.getAccessForReport(request?.id),
    options: { enabled: Boolean(request?.id), staleTime: ONE_MINUTE_MS },
  })

  const { data: accessorOrganizations } = useFetchResource<SimpleOrganization[]>({
    key: [FetchKey.AccessorOrganization],
    endpoint: endpoints.accessors,
    options: { staleTime: ONE_MINUTE_MS },
  })

  const isSharedWithNetwork = currentAccess?.isSharedWithTemplateAuthorizedOrgs ?? false

  const orgsWithAccess = (() => {
    if (!accessorOrganizations?.length || !currentAccess) return []
    if (currentAccess.organizationIds.length) {
      return accessorOrganizations.filter(org => currentAccess.organizationIds.includes(org.id))
    }
    const creatorId = request?.creatorOrganizationId ?? request?.creatorOrganization?.id
    return accessorOrganizations.filter(org => org.id === creatorId)
  })()

  if (!isSharedWithNetwork && !orgsWithAccess.length) return null

  return (
    <Stack mt={3} spacing={1}>
      <Typography variant="overline" color="text.secondary">
        {formatMessage({ id: 'assessment.sidebar.whoHasAccess' })}
      </Typography>
      {isSharedWithNetwork && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Avatar
            sx={{
              bgcolor: 'secondary.main',
              color: 'background.paper',
              borderRadius: '50%',
            }}
          >
            <Language />
          </Avatar>
          <Typography variant="body2" color="text.secondary">
            {formatMessage({ id: 'reporting.sharing.visibleToWfNetwork' })}
          </Typography>
        </Stack>
      )}
      {orgsWithAccess.length > 0 && (
        <List disablePadding>
          {orgsWithAccess.map(org => (
            <ListItem key={org.id} disablePadding sx={{ py: 0.5 }}>
              <ListItemAvatar sx={{ minWidth: 44 }}>
                <CompanyAvatar
                  organizationName={org.name}
                  imageUrl={org.image?.url}
                  sx={{ width: 32, height: 32, fontSize: 14 }}
                />
              </ListItemAvatar>
              <ListItemText primary={org.name} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      )}
    </Stack>
  )
}

export default SidebarAccessorsList
